'use client'
import { useRef, useState } from 'react'
import { Icon } from '@/components/Icons'
import posthog from 'posthog-js'
import { isInternalAnalyticsBrowser } from '@/lib/internal-analytics'
import { isMessageRequired, isValidPhone, MIN_MESSAGE } from '@/lib/contact'

interface FormState {
  name: string
  email: string
  subject: string
  message: string
  phone: string
}

const SUBMIT_LABELS: Record<string, string> = {
  trial: 'Proefperiode aanvragen',
  demo: 'Demo aanvragen',
}

const SUCCESS_TEXT: Record<string, (email: string, phone: string) => string> = {
  trial: (email, phone) => phone
    ? 'We bellen je uiterlijk de volgende werkdag om kennis te maken en je proefaccount klaar te zetten.'
    : `We mailen je uiterlijk de volgende werkdag op ${email} om kennis te maken en je proefaccount klaar te zetten.`,
  demo: email => `We mailen je uiterlijk de volgende werkdag op ${email} om een moment voor de demo van 30 minuten te kiezen.`,
}

interface ContactFormProps {
  initialSubject: string
  isFounding: boolean
}

/**
 * Waarom sommige onderwerpen niet in de lijst staan als je zomaar naar /contact gaat:
 * dit is een verkooppagina. Iemand die een demo komt aanvragen hoeft "Account
 * verwijderen" niet tussen de opties te zien. Ze verschijnen alleen als je er via de
 * bijbehorende pagina komt, die de query-parameter meestuurt (`?subject=deletion` vanaf
 * /account-verwijderen, `?subject=privacy` vanaf /privacy). Dat is hetzelfde
 * patroon als het founding-aanbod hierboven.
 *
 * Komt iemand tóch rechtstreeks binnen, dan kiest hij "Iets anders" en staat het in zijn
 * bericht. Dat kost het herkenbare onderwerp in de inbox, en dat is de prijs voor een
 * verkoopformulier dat niet over opzeggen gaat.
 *
 * "Melding over inhoud in de app" is bewust de uitzondering en staat er altijd. DSA art.
 * 16(1) verplicht aanbieders van hostingdiensten tot een meldmechanisme dat "easy to
 * access and user-friendly" is, en een optie die alleen bestaat achter de juiste
 * query-parameter is dat aantoonbaar niet. Art. 19 zondert alleen Sectie 3 (online
 * platforms) uit voor kleine ondernemingen; art. 16 staat in Sectie 2 en geldt ongeacht
 * omvang. Verbergen zou de code opleveren zonder de plicht te vervullen.
 */

export function ContactForm({ initialSubject, isFounding }: ContactFormProps) {
  const [form, setForm] = useState<FormState>({ name: '', email: '', subject: initialSubject, message: '', phone: '' })
  const [errors, setErrors] = useState<Record<string, string | null>>({})
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const idempotencyKey = useRef<string | null>(null)
  const startTracked = useRef(false)
  const messageRequired = isMessageRequired(form.subject)

  function update(field: keyof FormState, value: string) {
    if (!startTracked.current) {
      startTracked.current = true
      posthog.capture('contact_form_started', { subject: form.subject, first_field: field })
    }
    setForm(f => ({ ...f, [field]: value }))
    if (errors[field]) setErrors(e => ({ ...e, [field]: null }))
  }

  function validate(): Record<string, string> {
    const errs: Record<string, string> = {}
    if (!form.name.trim()) errs.name = 'Vul je naam in'
    if (!form.email.trim()) errs.email = 'Vul je e-mail in'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Geen geldig e-mailadres'
    const phone = form.phone.trim()
    if (phone && !isValidPhone(phone)) errs.phone = 'Geen geldig telefoonnummer'
    if (messageRequired && form.message.trim().length < MIN_MESSAGE) errs.message = `Bericht is te kort (min ${MIN_MESSAGE} tekens)`
    return errs
  }

  function ensureIdempotencyKey(): string {
    if (idempotencyKey.current) return idempotencyKey.current

    idempotencyKey.current = typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`
    return idempotencyKey.current
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length) {
      posthog.capture('contact_form_validation_failed', { subject: form.subject, fields: Object.keys(errs) })
      setErrors(errs)
      return
    }
    setLoading(true)
    setSubmitError(null)
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': ensureIdempotencyKey(),
        },
        body: JSON.stringify(form),
      })
      if (res.ok) {
        if (!isInternalAnalyticsBrowser()) {
          posthog.identify(form.email, { name: form.name, email: form.email })
        }
        posthog.capture('contact_form_submitted', { subject: form.subject, message_provided: form.message.trim().length > 0 })
        idempotencyKey.current = null
        setSubmitted(true)
      } else {
        posthog.capture('contact_form_error', { subject: form.subject, error_type: 'server_error' })
        setSubmitError('Er ging iets mis. Probeer het later opnieuw.')
      }
    } catch {
      posthog.capture('contact_form_error', { subject: form.subject, error_type: 'network_error' })
      setSubmitError('Geen verbinding. Controleer je internetverbinding en probeer het opnieuw.')
    } finally {
      setLoading(false)
    }
  }

  if (submitted) {
    return (
      <div className="form-success">
        <div className="form-success-icon"><Icon.CheckCircle/></div>
        <h3>Bericht verzonden!</h3>
        <p>
          Bedankt {form.name.split(' ')[0]}. {SUCCESS_TEXT[form.subject]?.(form.email, form.phone.trim()) ?? `We reageren uiterlijk de volgende werkdag via ${form.email}.`}
        </p>
        <button
          type="button"
          className="btn btn-secondary"
          style={{marginTop: 24}}
          onClick={() => {
            idempotencyKey.current = null
            startTracked.current = false
            setSubmitted(false)
            setForm({ name: '', email: '', subject: initialSubject, message: '', phone: '' })
          }}
        >Nog een bericht sturen</button>
      </div>
    )
  }

  return (
    <form className="form" onSubmit={handleSubmit} noValidate>
      <div className="form-row">
        <div className="field">
          <label className="field-label">Naam</label>
          <input
            className={`field-input ${errors.name ? 'invalid' : ''}`}
            placeholder="Anna de Vries"
            value={form.name}
            onChange={e => update('name', e.target.value)}
          />
          {errors.name && <div className="field-error">{errors.name}</div>}
        </div>
        <div className="field">
          <label className="field-label">E-mail</label>
          <input
            type="email"
            className={`field-input ${errors.email ? 'invalid' : ''}`}
            placeholder="anna@tennisschool.nl"
            value={form.email}
            onChange={e => update('email', e.target.value)}
          />
          {errors.email && <div className="field-error">{errors.email}</div>}
        </div>
      </div>
      <div className="field">
        <label className="field-label">Telefoon (optioneel)</label>
        <input
          type="tel"
          autoComplete="tel"
          className={`field-input ${errors.phone ? 'invalid' : ''}`}
          placeholder="06 12345678"
          aria-describedby={form.subject === 'trial' ? 'phone-hint' : undefined}
          value={form.phone}
          onChange={e => update('phone', e.target.value)}
        />
        {form.subject === 'trial' && !errors.phone && (
          <div id="phone-hint" className="field-hint">Liever gebeld dan gemaild? Laat je nummer achter.</div>
        )}
        {errors.phone && <div className="field-error">{errors.phone}</div>}
      </div>
      <div className="field">
        <label className="field-label">Onderwerp</label>
        <select
          className="field-input field-select"
          value={form.subject}
          onChange={e => update('subject', e.target.value)}
        >
          {isFounding && <option value="founding">Founding customer aanbod</option>}
          <option value="trial">Gratis 30 dagen test aanvragen</option>
          <option value="demo">Demo inplannen</option>
          <option value="custom">Voorstel op maat (meerdere locaties)</option>
          <option value="pricing">Vraag over prijzen</option>
          <option value="features">Vraag over features</option>
          <option value="migration">Migratie van huidige software</option>
          <option value="content">Melding over inhoud in de app</option>
          {form.subject === 'deletion' && <option value="deletion">Account verwijderen</option>}
          {form.subject === 'privacy' && <option value="privacy">Privacy of inzageverzoek</option>}
          <option value="other">Iets anders</option>
        </select>
      </div>
      <div className="field">
        <label className="field-label">Bericht{!messageRequired && ' (optioneel)'}</label>
        <textarea
          className={`field-textarea ${errors.message ? 'invalid' : ''}`}
          placeholder={messageRequired
            ? 'Vertel ons kort over je tennisschool: aantal trainers, groepen, en waar je nu mee worstelt.'
            : 'Bijvoorbeeld: aantal trainers en groepen, of waar je nu tegenaan loopt.'}
          value={form.message}
          onChange={e => update('message', e.target.value)}
        />
        {errors.message && <div className="field-error">{errors.message}</div>}
      </div>
      <button type="submit" className="btn btn-primary" disabled={loading} style={{alignSelf: 'flex-start', minWidth: 200, opacity: loading ? 0.7 : 1}}>
        {loading ? 'Verzenden…' : <><span>{SUBMIT_LABELS[form.subject] ?? 'Bericht verzenden'}</span> <Icon.Arrow size={16}/></>}
      </button>
      {submitError && (
        <p style={{ color: 'var(--error, #c0392b)', fontSize: 14, marginTop: 8 }}>
          {submitError}
        </p>
      )}
    </form>
  )
}
